import { supabase }              from '@/shared/lib/supabase';
import type { FutebolJogoDia, FutebolVotacaoJogo } from '@/shared/types';
import type { IVotacaoRepository }                 from '../domain/ports/IVotacaoRepository';
import { selecionarCandidatos }                    from '../domain/usecases/selecionarCandidatos';
import { calcularRangeDiaBRT }                     from '@/shared/lib/dateUtils';

function buildRows(candidatos: FutebolJogoDia[], hoje: string) {
  return candidatos.map((j) => ({
    data_votacao:     hoje,
    fixture_id:       j.api_fixture_id,
    time_casa:        j.time_casa,
    time_visitante:   j.time_visitante,
    escudo_casa:      j.escudo_casa,
    escudo_visitante: j.escudo_visitante,
    liga_nome:        j.liga_nome,
    votos:            0,
    vencedor:         false,
  }));
}

export const votacaoRepository: IVotacaoRepository = {
  async buscarCandidatosDoDia(): Promise<FutebolJogoDia[]> {
    // Todos os jogos do dia, inclusive finalizados: os grupos de votação são
    // recalculados a cada mount do Player, e tirar o jogo que acabou mudava o
    // grupo (reload no meio do 2º jogo reabria a votação com os perdedores).
    // Grupos 100% finalizados são ignorados no agendamento.
    const { inicio, fim } = calcularRangeDiaBRT();
    const { data, error } = await supabase
      .from('futebol_jogos_dia')
      .select('*')
      .gte('horario', inicio)
      .lt('horario', fim)
      .order('horario');
    if (error) throw error;
    return (data ?? []) as FutebolJogoDia[];
  },

  async iniciarVotacao(empresaId?: string): Promise<void> {
    const { inicio, fim, dataBRT: hoje } = calcularRangeDiaBRT();
    const { data, error: err } = await supabase
      .from('futebol_jogos_dia')
      .select('*')
      .in('status', ['pendente', 'em_andamento'])
      .gte('horario', inicio)
      .lt('horario', fim)
      .order('horario');
    if (err) throw err;

    // Painel manual: não exige televisionado_br para permitir ao admin iniciar
    // votação mesmo quando a flag não foi marcada na sincronização automática.
    const candidatos = selecionarCandidatos((data ?? []) as FutebolJogoDia[], 3, { exigirTv: false });

    if (candidatos.length === 0) {
      throw new Error('Nenhum jogo qualificado encontrado para hoje. Verifique se há jogos sincronizados com status pendente ou em_andamento.');
    }

    // Admin: abre a mesma votação em TODAS as telas da empresa (RPC via JWT).
    const { error } = await supabase.rpc('iniciar_votacao_admin' as never, {
      p_data_votacao: hoje,
      p_rows:         buildRows(candidatos, hoje),
      ...(empresaId ? { p_empresa_id: empresaId } : {}),
    } as never);
    if (error) throw error;
  },

  async iniciarVotacaoCiclo(candidatos: FutebolJogoDia[], telaId: string): Promise<void> {
    if (candidatos.length === 0) return;
    const { dataBRT: hoje } = calcularRangeDiaBRT();
    const fixtureIds        = candidatos.map((c) => c.api_fixture_id);
    const rows              = buildRows(candidatos, hoje);

    const { error } = await supabase.rpc('iniciar_votacao_ciclo' as never, {
      p_data_votacao: hoje,
      p_fixture_ids:  fixtureIds,
      p_rows:         rows,
      p_tela_id:      telaId,
    } as never);
    if (error) throw error;
  },

  async buscarVotacaoPorFixtureIds(fixtureIds: number[], telaId: string): Promise<FutebolVotacaoJogo[]> {
    const { dataBRT: hoje } = calcularRangeDiaBRT();
    const { data, error } = await supabase
      .from('futebol_votacao_jogos')
      .select('*')
      .eq('tela_id', telaId)
      .eq('data_votacao', hoje)
      .in('fixture_id', fixtureIds)
      .order('votos', { ascending: false })
      .order('fixture_id', { ascending: true });
    if (error) throw error;
    return (data ?? []) as FutebolVotacaoJogo[];
  },

  async buscarVotacaoHoje(telaId: string): Promise<FutebolVotacaoJogo[]> {
    const { dataBRT: hoje } = calcularRangeDiaBRT();
    const { data, error } = await supabase
      .from('futebol_votacao_jogos')
      .select('*')
      .eq('tela_id', telaId)
      .eq('data_votacao', hoje)
      .order('votos', { ascending: false });
    if (error) throw error;
    return (data ?? []) as FutebolVotacaoJogo[];
  },

  async votar(fixtureId: number, telaId: string, sessionId: string): Promise<void> {
    const { data, error } = await supabase.rpc(
      'incrementar_voto' as never,
      { p_fixture_id: fixtureId, p_session_id: sessionId, p_tela_id: telaId } as never,
    );
    if (error) throw error;
    if (data === 'ja_votou')        throw new Error('ja_votou');
    if (data === 'votacao_fechada') throw new Error('votacao_fechada');
  },

  async encerrarVotacao(empresaId?: string): Promise<void> {
    const { error } = await supabase.rpc('encerrar_votacao_admin' as never, {
      ...(empresaId ? { p_empresa_id: empresaId } : {}),
    } as never);
    if (error) throw error;
  },

  async encerrarVotacaoCiclo(fixtureIds: number[], telaId: string): Promise<number | null> {
    const { dataBRT: hoje } = calcularRangeDiaBRT();

    const { data, error } = await supabase.rpc('encerrar_votacao_ciclo' as never, {
      p_fixture_ids:  fixtureIds,
      p_data_votacao: hoje,
      p_tela_id:      telaId,
    } as never);
    if (error) throw error;

    return (data as number | null);
  },

  async ativarJogoSemVotacao(fixtureId: number, telaId: string): Promise<void> {
    // Caminho do Player anônimo (jogo único, sem votação): usa a RPC validada
    // ativar_jogo_unico — anon não tem grant em ativar_jogo_vencedor.
    const { error } = await supabase.rpc('ativar_jogo_unico' as never, {
      p_fixture_id: fixtureId,
      p_tela_id:    telaId,
    } as never);
    if (error) throw error;
  },
};
